import { Injectable } from "@angular/core";
import { Observable, catchError, map, shareReplay, throwError } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { Attribute } from "../../services/generated/waba-api/models";
import { AttributeApiService } from "../../services/generated/waba-api/services/attribute-api.service";

export interface AttributeCatalog {
  /** Everything except TAG-typed entries. */
  attributes: Attribute[];
  tags: Attribute[];
  byId: Map<number, Attribute>;
}

/**
 * Company attributes barely change but are needed by every contact filter,
 * form and picker. Fetching them per component made filter panels open empty
 * and re-hit the API each time they were toggled, so the response is shared
 * process-wide until something actually edits an attribute.
 */
@Injectable({ providedIn: "root" })
export class AttributeCatalogService {
  private cache$?: Observable<Attribute[]>;

  constructor(private attributeApiService: AttributeApiService) {}

  list(): Observable<Attribute[]> {
    if (!this.cache$) {
      this.cache$ = this.attributeApiService
        .listAllAttributes({
          "x-api-canal": "web",
          "x-api-requestId": uuidv4(),
        })
        .pipe(
          map((response) => {
            if ((response?.status && response.status !== "200") || !Array.isArray(response?.body)) {
              throw new Error(response?.message || "Unable to load contact attributes");
            }
            return response.body;
          }),
          catchError((error) => {
            // Never cache a failure, otherwise the whole session stays broken.
            this.cache$ = undefined;
            return throwError(() => error);
          }),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
    }
    return this.cache$;
  }

  /** Same data, pre-split the way the contact filters and forms consume it. */
  catalog(refresh = false): Observable<AttributeCatalog> {
    return (refresh ? this.refresh() : this.list()).pipe(
      map((all) => {
        const catalog: AttributeCatalog = {
          attributes: [],
          tags: [],
          byId: new Map<number, Attribute>(),
        };
        for (const attribute of all) {
          if (attribute.attributeType === "TAG") {
            catalog.tags.push(attribute);
          } else {
            catalog.attributes.push(attribute);
          }
          if (attribute.id != null) {
            catalog.byId.set(attribute.id, attribute);
          }
        }
        return catalog;
      }),
    );
  }

  /** Call after creating, editing or removing an attribute. */
  invalidate(): void {
    this.cache$ = undefined;
  }

  /**
   * Drop the cache and read again. Writes made in another tab never reach this
   * one's invalidate(), so screens where a missing attribute blocks the user
   * re-read on entry instead of trusting the cache.
   */
  refresh(): Observable<Attribute[]> {
    this.invalidate();
    return this.list();
  }
}
